import React, {
  FC, useMemo, useEffect, useCallback, useRef,
} from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Routes from 'Routes';
import ReactHtmlParser from 'react-html-parser';
import {
  useSelector, useDispatch, shallowEqual, DefaultRootState,
} from 'react-redux';
import { detect } from 'detect-browser';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { useMediaQuery } from '@material-ui/core';
import clamp from 'clamp-js';
import { selectLocale, selectAvailableLocales } from 'models/localization';
import {
  fetchBoothById,
  selectFetchingStateByBoothId,
  BoothFetchingState,
  BoothFetchedLevel,
  selectBoothById,
} from 'models/cms/booths';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import pickFieldFromCMSBoothTargetBooth from 'utils/pickFieldFromCMSBoothTargetBooth';
import useBoothTier from 'hooks/useBoothTier';

import BoothBackground from '../../booths/BoothTemplateSwitch';
import BoothTierLabelAutoScroll from './BoothTierLabelAutoScroll';
import { liveStreamingStatusSelector } from '../../../models/liveStream';

const BOOTH_BASE_WIDTH = 330;
const BOOTH_BASE_HEIGHT = 276;
const DEFAULT_SCALE = 0.25;

const BoothCompanyName = styled.div<{ $tierAvailable: boolean, isIe: boolean }>`
  width: 100%;
  min-height: 30px;
  max-height: 60px;
  text-align: center;
  font-weight: bold;
  line-height: 29px;
  color: #484848;
  margin: ${({ $tierAvailable }) => ($tierAvailable ? 40 : 24)}px auto 4px auto;
  padding: 0 16px;

  ${({ isIe }) => (isIe
    && `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    `
  )}
`;

const BoothInfoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  position: relative;
  padding: 16px;
  padding-top: 0;
`;

const BoothDescription = styled.div`
  position: absolute;
  width: 100%;
  height: calc(100% - 16px);
  word-break: break-word;
  transition: all 0.3s;
  opacity: 0;
  overflow: hidden;
  text-align: center;
  padding: 16px;
  padding-top: 0;
  top: 0;
  left: 0;

  & > * {
    margin: 0;
    padding: 0;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 25%;
  max-height: 50px;
  background-image:
    linear-gradient(
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.8) 75%,
      rgba(255, 255, 255, 1) 100%
    );
`;

const BoothLogo = styled.img`
  max-height: 80%;
  max-width: 80%;
  object-fit: contain;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  width: auto;
  display: block;
`;

const BoothPreviewImage = styled.img`
  max-height: 80%;
  max-width: 80%;
  object-fit: contain;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
  width: auto;
  display: block;
`;

const BoothImageWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  margin: auto;
`;

const BoothBackgroundImageContainer = styled.div<{ $multiplier: number }>`
  height: intrinsic;
  margin-bottom: 16px;
  position: absolute;
  z-index: 0;
  left: 50%;
  top: 50%;
  transform: scale(${({ $multiplier }) => $multiplier * DEFAULT_SCALE}) translate(-50%, -50%);
  transform-origin: top left;
  opacity: 1;

  .EyeCatchContainer {
    top: -50px;
    left: 50%;
  }
`;

const BoothIsLiveTag = styled.div`
  position: absolute;
  bottom: 30px;
  right: 30px;
  padding: 4px;
  border-radius: 5px;
  background: red;
  color: white;
`;

interface LinkContainerProps {
  $multiplier: number;
  $hideDescription: boolean;
  $simpleBooth: boolean;
}

const StyledLink = styled(Link)<LinkContainerProps>`
  width: ${({ $multiplier }) => BOOTH_BASE_WIDTH * $multiplier}px;
  height: ${({ $multiplier }) => BOOTH_BASE_HEIGHT * $multiplier}px;
  
  color: #484848;
  text-decoration: none;
  margin: 20px 24px;
  border-radius: 8px;

  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: background-color 0.15s;

  display: flex;
  flex-direction: column;
  position: relative;

  :hover {
    background-color: rgba(255, 255, 255, 1);
    ${BoothDescription} {
      opacity: ${({ $hideDescription, $simpleBooth }) => (($hideDescription || $simpleBooth) ? 0 : 1)}
    }
    ${BoothPreviewImage} {
      opacity: ${({ $hideDescription }) => ($hideDescription ? 1 : 0)}
    }
    ${BoothBackgroundImageContainer} {
      opacity: ${({ $hideDescription }) => ($hideDescription ? 1 : 0)}
    }
  }
`;

const selectFetching = (boothId) => (state) => {
  const fetchingState = selectFetchingStateByBoothId(state, boothId);
  return !fetchingState || fetchingState === BoothFetchingState.Pending;
};

export interface GenericBoothPreviewProps {
  boothId: string;
  index: number;
  enableTier?: boolean;
}

const GenericBoothPreview: FC<GenericBoothPreviewProps> = ({ boothId, index, enableTier }) => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const browser = detect();
  const availableLocales = useSelector(selectAvailableLocales);
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const boothNameRef = useRef(null);
  const isFetching = useSelector(selectFetching(boothId), shallowEqual);
  const { features, hall: { simpleBoothPreview } } = useSelector((s) => s.eventConfigs, shallowEqual);
  const isSimpleBoothPreview = simpleBoothPreview ?? features.includes('simpleBoothPreview');

  const boothSelector = useCallback((state: DefaultRootState) => selectBoothById(state, boothId), [boothId]);
  const booth = useSelector(boothSelector, shallowEqual);

  const { tierName, tierScale } = useBoothTier(enableTier && booth?.tier?.id);
  const boothTierName = useMemo(() => findAvailableLocale(tierName, locale), [tierName, locale]);
  const scale = isDesktop ? tierScale : 1.0;

  const boothName = useMemo(() => pickFieldFromCMSBoothTargetBooth(booth, 'name', [locale, ...availableLocales]), [booth, locale, availableLocales]);
  const boothDescription = useMemo(() => pickFieldFromCMSBoothTargetBooth(booth, 'description', [locale, ...availableLocales]) as string, [booth, locale, availableLocales]);

  const hideDescription = useMemo(() => !boothDescription, [boothDescription]);
  const boothLogoSrc = useMemo(() => findAvailableLocale(booth.logo, locale)?.originalImageUrl, [booth.logo, locale]);
  const boothPreviewImage = booth?.boothDesignCanvas?.previewImage?.originalImageUrl;
  const isV2Design = useMemo(() => !!booth.boothDesign?.id, [booth.boothDesign?.id]);

  const showPreviewImage = !!boothPreviewImage && isV2Design;
  const isLiveStreaming = useSelector(liveStreamingStatusSelector(boothId));
  const isIe = useMemo(() => (browser?.name === 'ie'), [browser.name]);

  useEffect(() => {
    if (browser?.name !== 'ie') {
      clamp(boothNameRef.current as HTMLElement, { clamp: 2 });
    }
  }, [boothNameRef, browser?.name]);

  useEffect(() => {
    if (!boothId || isSimpleBoothPreview) return () => null;
    const expectedLevel = showPreviewImage ? BoothFetchedLevel.Meta : BoothFetchedLevel.Design;
    const timer = setTimeout(() => {
      dispatch(fetchBoothById({
        boothId,
        expectedLevel,
      }));
    }, 500 + ((index * 16) % 250));
    return () => clearTimeout(timer);
  }, [boothId, booth.$lastReceivedAt, dispatch, isSimpleBoothPreview, index, locale, showPreviewImage]);

  return (
    <StyledLink
      to={Routes.booth(booth.id)}
      className="booth-preview-link"
      $multiplier={scale}
      $simpleBooth={isSimpleBoothPreview}
      $hideDescription={hideDescription}
    >
      <BoothCompanyName
        ref={boothNameRef}
        className="booth-preview-company-name"
        $tierAvailable={Boolean(boothTierName)}
        isIe={isIe}
      >
        {boothName}
      </BoothCompanyName>
      <BoothInfoWrapper>
        {(!isFetching || isSimpleBoothPreview) ? (
          <>
            <BoothDescription className="booth-preview-description">
              {ReactHtmlParser(boothDescription)}
              <GradientOverlay />
            </BoothDescription>
            {isSimpleBoothPreview && <BoothLogo className="booth-logo" src={boothLogoSrc} alt={`Logo of ${boothName}`} />}
            {!isSimpleBoothPreview && showPreviewImage && <BoothPreviewImage className="booth-preview-image" src={boothPreviewImage} />}
            {!isSimpleBoothPreview && !showPreviewImage && (
              <BoothImageWrapper className="booth-preview-image-wrapper">
                <BoothBackgroundImageContainer $multiplier={scale}>
                  <BoothBackground booth={booth} isPreview />
                </BoothBackgroundImageContainer>
              </BoothImageWrapper>
            )}
          </>
        ) : (
          <CircularLoadingIndicator size={30} disableShrink />
        )}
      </BoothInfoWrapper>
      {boothTierName && <BoothTierLabelAutoScroll label={boothTierName} />}
      {isLiveStreaming && <BoothIsLiveTag>LIVE NOW</BoothIsLiveTag>}
    </StyledLink>
  );
};

export default React.memo(GenericBoothPreview, (prev, next) => prev.boothId === next.boothId);
